<template>
  <!-- Slider -->
  <div class="w-full mx-auto py-10" v-show="!selectedAccount">
    <Swiper
        :centered-slides="true"
        :grab-cursor="true"
        :loop="true"
        :pagination="{ clickable: true }"
        :slides-per-view="3"
        :space-between="20"
        class="mySwiper"
    >
      <SwiperSlide
          v-for="(card, index) in cards"
          :key="index"
          class="transition-transform duration-300"
      >
        <div
            @click="selectAccount(card)"
            class="bg-white rounded-2xl shadow-lg p-6 flex flex-col items-center justify-center text-xl font-bold h-60 cursor-pointer hover:shadow-xl transition"
        >
          <!-- Account info -->
          <div class="flex flex-col">
            <div class="flex items-center space-x-2">
              <h1 class="text-lg font-medium text-primary">{{ card.account_name }}</h1>
            </div>
            <div class="mt-1">
              <span class="inline-block bg-gray-800 text-white text-sm px-3 py-1 rounded-md">
                {{ card.account_number }}
              </span>
            </div>
            <div class="flex justify-center space-x-2">
              <p class="text-lg text-gray-600 text-center font-normal">EUR {{ formatNumber(card.available_balance) }}</p>
            </div>
          </div>
        </div>
      </SwiperSlide>
    </Swiper>
  </div>

  <!-- Selected Account Details -->
  <div class="px-6 py-10" v-if="selectedAccount">
    <div class="p-4">
      <div class="flex items-center justify-between">
        <!-- Left side: dropdown -->
        <div class="flex flex-col items-left relative">
          <button @click="dropdownOpen = !dropdownOpen" class="flex items-center space-x-2 text-gray-800 font-medium">
            <span>{{ selectedAccount.account_name }} ({{ selectedAccount.account_number }})</span>
            <svg class="w-4 h-4 text-gray-500 transform" :class="{ 'rotate-180': dropdownOpen }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>

          <!-- Dropdown list -->
          <div v-if="dropdownOpen" class="absolute z-10 mt-2 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
            <div
                v-for="acc in cards"
                :key="acc.account_number"
                @click="selectAccount(acc); dropdownOpen = false"
                class="px-4 py-2 hover:bg-gray-100 cursor-pointer"
            >
              {{ acc.account_name }} ({{ acc.account_number }})
            </div>
          </div>
        </div>

        <!-- Right side: balances -->
        <div class="flex space-x-8">
          <div class="text-right">
            <div class="text-lg font-medium text-gray-800">{{ formatNumber(selectedAccount.accounting_balance) }}</div>
            <div class="text-sm text-primary">Accounting</div>
          </div>
          <div class="text-right">
            <div class="text-lg font-medium text-gray-800">{{ formatNumber(selectedAccount.available_balance) }}</div>
            <div class="text-sm text-primary">Available</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="flex justify-center space-x-4 mb-6">
      <button
          v-for="item in items"
          :key="item.id"
          :class="[
          'flex flex-col items-center bg-white px-6 py-4 border rounded-lg shadow-md cursor-pointer transition-colors hover:border-gray-400 hover:bg-white hover:shadow-md hover:text-gray-800',
          selectedTab === item.id ? 'border-gray-400 bg-white shadow-md' : 'border-transparent text-primary'
        ]"
          @click="selectedTab = item.id"
      >
        <component :is="item.icon" class="w-8 h-8 mb-2"/>
        <span :class="selectedTab === item.id ? 'text-gray-800' : 'text-orange-400'" class="text-sm">
          {{ item.label }}
        </span>
      </button>
    </div>

    <!-- Selected content -->
    <div>
      <div>
        <component
            :is="currentComponent"
            :account="selectedAccount"
            :key="selectedTab + (selectedAccount?.account_number || '')"
            @step-three-reached="handleStepThree"
        />
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted,watch } from 'vue';
import {ArrowLeftRight, FileText, Logs} from 'lucide-vue-next';
import { Swiper, SwiperSlide } from 'swiper/vue';
import SwiperCore, { Pagination, Navigation } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

// import ViewBalance from "@/components/view/ViewBalance";
import ViewMovement from "@/components/view/ViewMovement";
import ViewDetail from "@/components/view/ViewDetail";
import ViewTransferPage from "@/components/view/ViewTransferPage.vue";
import api from '@/services/api';
import {formatNumber} from "../utils/number";
import { useRoute } from 'vue-router';
import ViewBalance from "@/components/view/ViewBalance.vue";
const route = useRoute();
SwiperCore.use([Pagination, Navigation]);

// State
const cards = ref([]);
const selectedAccount = ref(null);
const selectedTab = ref('Movements');
const dropdownOpen = ref(false)


const items = [
  { id: 'Balance', label: 'Balance', icon: Logs, component: ViewBalance },
  { id: 'Movements', label: 'Movements', icon: Logs, component: ViewMovement },
  { id: 'Details', label: 'Details', icon: FileText, component: ViewDetail },
  { id: 'Transfers', label: 'Transfers', icon: ArrowLeftRight , component: ViewTransferPage },
];

const currentComponent = computed(() => {
  const found = items.find(i => i.id === selectedTab.value);
  return found ? found.component : null;
});

watch(selectedTab, async (newTab) => {
  if (newTab === 'Movements' && selectedAccount.value) {
    await fetchAccounts(selectedAccount.value.account_number);
  }
});



const handleStepThree = async () => {
  // موجودی حساب‌ها رو آپدیت کن
  try {
    const res = await api.get('/accounts', {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });
    cards.value = res.data
    if (cards.value.length > 0) {
      selectedAccount.value = cards.value[0]
    }
    console.log('Accounts updated after step 3');
  } catch (err) {
    console.error('Error fetching accounts:', err);
  }
};


// Fetch accounts + movements
const fetchAccounts = async () => {
  try {
    const res = await api.get('/accounts/details', {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });

    console.log("fetchAccounts",res);
    cards.value = res.data.map(acc => ({ ...acc }));
    if (cards.value.length > 0) {
      selectedAccount.value = cards.value[0];
      // selectedTab.value = 'Movements';
    }



  } catch (err) {
    console.error('Failed to fetch accounts:', err);
  }
};

// Select account
// const selectAccount = (accountNumber) => {
//   const account = cards.value.find(a => a.account_number === accountNumber);
//   if (account) {
//     selectedAccount.value = account;
//     selectedTab.value = 'Movements';
//   }
// };

// const selectAccount = (account) => {
//   selectedAccount.value = account
//   selectedTab.value = 'Movements';
// }


const selectAccount = (account, defaultTab = 'Movements') => {
  selectedAccount.value = account;
  selectedTab.value = defaultTab;
};


onMounted(async () => {
  // 1️⃣ fetch accounts
  await fetchAccounts();

  // 2️⃣ بررسی query برای تب
  const tabFromQuery = route.query.defaultTab;
  if (tabFromQuery) {
    selectedTab.value = tabFromQuery;
  }

  // 3️⃣ بررسی query برای account
  const accountFromQuery = route.query.account;
  if (accountFromQuery) {
    const foundAccount = cards.value.find(acc => acc.account_number === accountFromQuery);
    if (foundAccount) {
      selectedAccount.value = foundAccount;
      selectedTab.value = route.query.defaultTab || 'Movements';
    }
  }
});


</script>
